import { JobTaskSchema } from "@augmodo/lebron-browser-client";
import { configureStore, createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Match {
  empty_area_index: number;
  product_proposal_index: number;
  remaining_faces: boolean;
  reference_image_index?: number;
}

interface OosState {
  activeModal: string | null;
  matches: Match[];
  selectedEmptyArea: number | null;
  selectedProductProposal: number | null;
  task: JobTaskSchema | null;
  activeReferenceImageIndex: number;
  emptyAreaConfirmed: boolean | null;
  itemDetectionConfirmed: boolean | null;
  productMatchConfirmed: boolean | null;
  qaQuestionOneCorrect: boolean;
  qaQuestionOneIncorrectParts: string[];
  qaQuestionTwoCorrect: boolean;
  qaQuestionThreeCorrect: boolean;
  searchUsername: string;
  groupedEventsIndex: number | null;
  selectedProductId: string | null;
  selectedProductIds: string[];
  resultMetadata: Record<string, any>;
  planogramOverrideId: string | null;
  planogramSelectedProductId: string | null;
  planogramModalSource: "product" | "empty_area" | null;
  planogramModalUsed: boolean;
  isPlayMode: boolean;
  selectedRetailerId: string | null;
  // Ordered queue of the current user's assigned tasks for "My Tasks" play
  // mode. Each entry points at a specific task; play mode navigates to it via
  // ?taskId= and walks through them in order.
  taskPlaylist: { jobId: string; taskId: string; route: string }[];
  playlistIndex: number;
}

const initialOosState: OosState = {
  activeModal: null,
  matches: [],
  selectedEmptyArea: null,
  selectedProductProposal: null,
  task: null,
  activeReferenceImageIndex: 0,
  emptyAreaConfirmed: null,
  itemDetectionConfirmed: null,
  productMatchConfirmed: null,
  qaQuestionOneCorrect: true,
  qaQuestionOneIncorrectParts: [],
  qaQuestionTwoCorrect: true,
  qaQuestionThreeCorrect: true,
  searchUsername: "",
  groupedEventsIndex: null,
  selectedProductId: null,
  selectedProductIds: [],
  resultMetadata: {
    not_main_aisle: false,
    not_empty_area: false,
    unblurred_person_in_image: false,
    remaining_product_facings: false,
    blue_tag_present: false,
  },
  planogramOverrideId: null,
  planogramSelectedProductId: null,
  planogramModalSource: null,
  planogramModalUsed: false,
  isPlayMode: false,
  selectedRetailerId: localStorage.getItem("oos-retailer-id") || null,
  taskPlaylist: [],
  playlistIndex: 0,
};

const oosSlice = createSlice({
  name: "oos",
  initialState: initialOosState,
  reducers: {
    setActiveModal(state: OosState, action: PayloadAction<string | null>) {
      state.activeModal = action.payload;
    },
    setSelectedEmptyArea(
      state: OosState,
      action: PayloadAction<number | null>,
    ) {
      state.selectedEmptyArea = action.payload;
    },
    setSelectedProductProposal(
      state: OosState,
      action: PayloadAction<number | null>,
    ) {
      state.selectedProductProposal = action.payload;
    },
    setMatches(state: OosState, action: PayloadAction<Match[]>) {
      state.matches = action.payload;
    },
    setTask(state: OosState, action: PayloadAction<JobTaskSchema | null>) {
      state.task = action.payload;
    },
    setActiveReferenceImageIndex(
      state: OosState,
      action: PayloadAction<number>,
    ) {
      state.activeReferenceImageIndex = action.payload;
    },
    setEmptyAreaConfirmed(
      state: OosState,
      action: PayloadAction<boolean | null>,
    ) {
      state.emptyAreaConfirmed = action.payload;
    },
    setItemDetectionConfirmed(state: OosState, action: PayloadAction<boolean>) {
      state.itemDetectionConfirmed = action.payload;
    },
    setProductMatchConfirmed(
      state: OosState,
      action: PayloadAction<boolean | null>,
    ) {
      state.productMatchConfirmed = action.payload;
    },
    setQaQuestionOneCorrect(state: OosState, action: PayloadAction<boolean>) {
      state.qaQuestionOneCorrect = action.payload;
      // Marking the question correct again clears any selected wrong parts.
      if (action.payload) {
        state.qaQuestionOneIncorrectParts = [];
      }
    },
    setQaQuestionOneIncorrectParts(
      state: OosState,
      action: PayloadAction<string[]>,
    ) {
      state.qaQuestionOneIncorrectParts = action.payload;
    },
    setQaQuestionTwoCorrect(state: OosState, action: PayloadAction<boolean>) {
      state.qaQuestionTwoCorrect = action.payload;
    },
    setQaQuestionThreeCorrect(state: OosState, action: PayloadAction<boolean>) {
      state.qaQuestionThreeCorrect = action.payload;
    },
    setSearchUsername(state: OosState, action: PayloadAction<string>) {
      state.searchUsername = action.payload;
    },
    setGroupedEventsIndex(
      state: OosState,
      action: PayloadAction<number | null>,
    ) {
      state.groupedEventsIndex = action.payload;
    },
    setSelectedProductId(
      state: OosState,
      action: PayloadAction<string | null>,
    ) {
      state.selectedProductId = action.payload;
    },
    setSelectedProductIds(state: OosState, action: PayloadAction<string[]>) {
      state.selectedProductIds = action.payload;
    },
    setResultMetadata(
      state: OosState,
      action: PayloadAction<Record<string, any>>,
    ) {
      state.resultMetadata = action.payload;
    },
    setPlanogramOverrideId(
      state: OosState,
      action: PayloadAction<string | null>,
    ) {
      state.planogramOverrideId = action.payload;
    },
    setPlanogramSelectedProductId(
      state: OosState,
      action: PayloadAction<string | null>,
    ) {
      state.planogramSelectedProductId = action.payload;
    },
    setPlanogramModalSource(
      state: OosState,
      action: PayloadAction<"product" | "empty_area" | null>,
    ) {
      state.planogramModalSource = action.payload;
    },
    setPlanogramModalUsed(state: OosState, action: PayloadAction<boolean>) {
      state.planogramModalUsed = action.payload;
    },
    setIsPlayMode(state: OosState, action: PayloadAction<boolean>) {
      state.isPlayMode = action.payload;
    },
    setTaskPlaylist(
      state: OosState,
      action: PayloadAction<{ jobId: string; taskId: string; route: string }[]>,
    ) {
      state.taskPlaylist = action.payload;
      state.playlistIndex = 0;
    },
    setPlaylistIndex(state: OosState, action: PayloadAction<number>) {
      state.playlistIndex = action.payload;
    },
    clearTaskPlaylist(state: OosState) {
      state.taskPlaylist = [];
      state.playlistIndex = 0;
    },
    setSelectedRetailerId(
      state: OosState,
      action: PayloadAction<string | null>,
    ) {
      state.selectedRetailerId = action.payload;
      if (action.payload) {
        localStorage.setItem("oos-retailer-id", action.payload);
      } else {
        localStorage.removeItem("oos-retailer-id");
      }
    },
    reset(state) {
      return {
        ...initialOosState,
        isPlayMode: state.isPlayMode,
        selectedRetailerId: state.selectedRetailerId,
        // Preserve the play-mode queue across per-task resets so My Tasks play
        // mode can keep advancing.
        taskPlaylist: state.taskPlaylist,
        playlistIndex: state.playlistIndex,
      };
    },
  },
});

export const store = configureStore({
  reducer: {
    oos: oosSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const {
  setActiveModal,
  setMatches,
  setSelectedEmptyArea,
  setSelectedProductProposal,
  setTask,
  setActiveReferenceImageIndex,
  setEmptyAreaConfirmed,
  setItemDetectionConfirmed,
  setProductMatchConfirmed,
  setQaQuestionOneCorrect,
  setQaQuestionOneIncorrectParts,
  setQaQuestionTwoCorrect,
  setQaQuestionThreeCorrect,
  setSearchUsername,
  setGroupedEventsIndex,
  setSelectedProductId,
  setSelectedProductIds,
  setResultMetadata,
  setPlanogramOverrideId,
  setPlanogramSelectedProductId,
  setPlanogramModalSource,
  setPlanogramModalUsed,
  setIsPlayMode,
  setSelectedRetailerId,
  setTaskPlaylist,
  setPlaylistIndex,
  clearTaskPlaylist,
  reset,
} = oosSlice.actions;
